import { Box, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, Grid, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useFormikContext } from 'formik';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { ExtendedRuleDefinition, ConversionType } from '../../gen/api/api';
import useFieldHelpers from '../../helpers/fieldHelper';
import useFeatures from '../../helpers/features';
import { WidthType } from '../../shared/settings/config';
import useTSHacks from '../../shared/tshacks/tshacks';
import useConfigurationForm from '../ConfigurationForm/ConfigurationFormHooks';
import useRuleComponents from './RuleComponentHooks';

interface EarnAndBurnRuleComponentProps {
    readonly ruleDefinition: ExtendedRuleDefinition;
    readonly width: WidthType;
    readonly disabled: boolean;
}

const earnOrBurn = {
    earnOnly: 'earn-only',
    burnOnly: 'burn-only',
    earnAndBurn: 'earn-and-burn',
    any: 'any',
};

const defaultRoundingOption = 'Unnecessary'

const { getObjectProperty } = useTSHacks();

const { featureEnabledGrocerPointsConversion } = useFeatures();

const EarnAndBurnRuleComponent: FunctionComponent<EarnAndBurnRuleComponentProps> = ({ ...props }) => {
    const { renderFields } = useRuleComponents();

    const { generateFieldNameFromCodes } = useFieldHelpers();

    const { isEarnOnlyAndNotEarnAndBurnFact } = useConfigurationForm();

    const earnOrBurnFieldName = generateFieldNameFromCodes('optin.rules.earnburn', 'earnOrBurn');

    const earnRoundingSettingsFieldName = generateFieldNameFromCodes('optin.rules.earnburn', 'earnRoundingSettings');

    const burnRoundingSettingsFieldName = generateFieldNameFromCodes('optin.rules.earnburn', 'burnRoundingSettings');

    const { values, setFieldValue, handleChange } = useFormikContext();

    const [expanded, setExpanded] = useState<string | false>(false);

    const conversionType: ConversionType = getObjectProperty(values, 'type');

    const isCurrencyConversion = () => {
        return conversionType === ConversionType.CURRENCYCONVERSION
    }

    const isGrocerPointsConversion = () => {
        return conversionType === ConversionType.GROCERPOINTSCONVERSION
    }

    const isFisPointsConversion = () => {
        return conversionType === ConversionType.FISCONVERSION
    }

    const handleExpansionPanelChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        // handling only expanded=true to prevent collapsing of the expansion panels
        if (isExpanded) {
            setExpanded(panel);
            if (earnOrBurn.earnOnly === panel || earnOrBurn.burnOnly === panel) {
                setFieldValue(earnOrBurnFieldName, panel);
            } else {
                setFieldValue(earnOrBurnFieldName, earnOrBurn.any);
            }
            handleChange(event);
        }
    };

    useEffect(() => {
        const earnOrBurnValue = getObjectProperty(values, earnOrBurnFieldName);
        if (earnOrBurnValue === "") {
            // empty string can be achieved either by clicking clear in dropdowns, or deleting text in autocomplete textbox
            setFieldValue(earnOrBurnFieldName, earnOrBurn.any);
        } else if (!earnOrBurnValue) {
            // undefined should be only when creating new roc entry
            setFieldValue(earnOrBurnFieldName, earnOrBurn.burnOnly);
            setExpanded(earnOrBurn.burnOnly);
        } else if ((isGrocerPointsConversion() || isFisPointsConversion()) && earnOrBurnValue !== earnOrBurn.burnOnly) {
            setFieldValue(earnOrBurnFieldName, earnOrBurn.burnOnly);
            setExpanded(earnOrBurn.burnOnly);
        } else if (earnOrBurn.earnOnly === earnOrBurnValue || earnOrBurn.burnOnly === earnOrBurnValue) {
            setExpanded(earnOrBurnValue);
        } else {
            setExpanded(earnOrBurn.earnAndBurn);
        }
        const earnRoundingSettingsValue = getObjectProperty(values, earnRoundingSettingsFieldName);
        if (!earnRoundingSettingsValue) {
            setFieldValue(earnRoundingSettingsFieldName, defaultRoundingOption);
        }
        const burnRoundingSettingsValue = getObjectProperty(values, burnRoundingSettingsFieldName);
        if (!burnRoundingSettingsValue) {
            setFieldValue(burnRoundingSettingsFieldName, defaultRoundingOption);
        }

    }, [values]);

    return (
        <Grid item xs={props.width} key={props.ruleDefinition.ruleCode}>
            <Box p={1} m={1} boxShadow={3}>
                <Typography>{props.ruleDefinition.ruleName}</Typography>
                { isCurrencyConversion() &&
                <ExpansionPanel
                    expanded={expanded === earnOrBurn.earnOnly}
                    onChange={handleExpansionPanelChange(earnOrBurn.earnOnly)}
                    id={earnOrBurn.earnOnly}
                    disabled={props.disabled}
                >
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="earn-only-content"
                        id="earn-only-header"
                    >
                        <Typography>Earn Only</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails key="details">
                        <Grid container spacing={1}>
                            {renderFields(
                                props.ruleDefinition,
                                Object.values(props.ruleDefinition.factDefinitions).filter((def) =>
                                    isEarnOnlyAndNotEarnAndBurnFact(def.factCode),
                                ),
                                '',
                                props.disabled,
                            )}
                        </Grid>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
                }
                { (isCurrencyConversion() || isGrocerPointsConversion() || isFisPointsConversion()) &&
                <ExpansionPanel
                    expanded={expanded === earnOrBurn.burnOnly}
                    onChange={handleExpansionPanelChange(earnOrBurn.burnOnly)}
                    id={earnOrBurn.burnOnly}
                    disabled={props.disabled}
                >
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="burn-only-content"
                        id="burn-only-header"
                    >
                        <Typography>Burn Only</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails key="details">
                        <Grid container spacing={1}>
                            {renderFields(
                                props.ruleDefinition,
                                Object.values(props.ruleDefinition.factDefinitions).filter((def) =>
                                    def.factCode.startsWith('burn'),
                                ),
                                '',
                                props.disabled,
                            )}
                        </Grid>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
                }
                { isCurrencyConversion() &&
                 <ExpansionPanel
                    expanded={expanded === earnOrBurn.earnAndBurn}
                    onChange={handleExpansionPanelChange(earnOrBurn.earnAndBurn)}
                    id={earnOrBurn.earnAndBurn}
                    disabled={props.disabled}
                >
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="earn-and-burn-content"
                        id="earn-and-burn-header"
                    >
                        <Typography>Earn and Burn</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails key="details">
                        <Grid container spacing={1}>
                            {renderFields(
                                props.ruleDefinition,
                                Object.values(props.ruleDefinition.factDefinitions),
                                earnOrBurn.earnAndBurn,
                                props.disabled,
                            )}
                        </Grid>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
                }
            </Box>
        </Grid>
    );
};

export default EarnAndBurnRuleComponent;
